import { useInfiniteQuery } from '@tanstack/react-query';
import {
  REPORT_PERIOD_LIMIT_DAYS,
  type PharmacyRegisterRow,
  type ReportPeriod,
} from '@wodi/shared';
import { Button, PrintTable, Select } from '@wodi/ui';
import { useState } from 'react';

import { TableScroll } from '../layout/screen.tsx';
import { fetchPharmacyRegister, reportKeys, type PharmacyQuery } from '../api/reports.ts';
import { formatInstant } from '../patients/format.ts';
import { plural } from '../reports/labels.ts';
import {
  AsAt,
  PeriodFields,
  periodIsValid,
  periodWords,
  PrintableReport,
  ReportActions,
  ReportStates,
  todayIso,
  useSheet,
} from '../reports/report-frame.tsx';
import styles from '../reports/reports.module.css';
import { MoreButton, PAGE_SIZE, RegisterShell } from './report-registers.tsx';

// The pharmacy register (plan §2.10): every medicine handed over at the facility the
// reader works at, dispensed against a prescription or sold at the counter, up to 31 days
// (P8). It lists patients, so the server leaves out visits with labels the reader isn't
// granted and audits every view; downloads warn before they start.

const KIND_OPTIONS = [
  { id: 'any', label: 'Dispensed and sold' },
  { id: 'dispense', label: 'Dispensed only' },
  { id: 'sale', label: 'Counter sales only' },
];

const KIND_WORDS = { dispense: 'Dispensed', sale: 'Counter sale' } as const;

export function PharmacyRegisterPage({ day }: { day?: string | undefined }) {
  const start = day ?? todayIso();
  const [period, setPeriod] = useState<ReportPeriod>({ from: start, to: start });
  const [kind, setKind] = useState('any');
  const [shown, setShown] = useState<PharmacyQuery>({ period });
  const register = useInfiniteQuery({
    queryKey: reportKeys.report('pharmacy-register', shown),
    queryFn: ({ pageParam }) =>
      fetchPharmacyRegister({
        ...shown,
        limit: PAGE_SIZE,
        ...(pageParam === null ? {} : { cursor: pageParam }),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const sheet = useSheet();
  const valid = periodIsValid(period, REPORT_PERIOD_LIMIT_DAYS.register);
  const first = register.data?.pages[0];
  const rows = register.data?.pages.flatMap((p) => p.rows) ?? [];
  return (
    <RegisterShell
      title="Pharmacy register"
      filters={
        <form
          className={styles.filters}
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            setShown({
              period,
              ...(kind === 'dispense' || kind === 'sale' ? { kind } : {}),
            });
          }}
        >
          <PeriodFields
            period={period}
            onChange={setPeriod}
            maxDays={REPORT_PERIOD_LIMIT_DAYS.register}
          />
          <Select
            label="Show"
            options={KIND_OPTIONS}
            value={kind}
            onChange={(v) => {
              setKind(String(v));
            }}
          />
          <div>
            <Button type="submit" variant="primary" isDisabled={!valid}>
              Show report
            </Button>
          </div>
        </form>
      }
    >
      <ReportStates
        query={register}
        what="register"
        isEmpty={(d) => (d.pages[0]?.rows.length ?? 0) === 0}
        emptyText={`No medicines dispensed or sold on ${periodWords(shown.period)}.`}
      >
        {(d) => (
          <>
            <AsAt dataAsAt={d.pages[0]?.dataAsAt ?? new Date().toISOString()} />
            <p className={styles.muted}>
              {`${plural(rows.length, 'line', 'lines')} shown${register.hasNextPage ? ', more to load' : ''}.`}
            </p>
            <TableScroll>
              <table className={styles.table} aria-label="Pharmacy register">
                <PharmacyHead />
                <tbody>
                  {rows.map((r) => (
                    <PharmacyRow key={r.lineId} row={r} />
                  ))}
                </tbody>
              </table>
            </TableScroll>
            <MoreButton
              hasMore={register.hasNextPage}
              loading={register.isFetchingNextPage}
              onMore={() => void register.fetchNextPage()}
            />
          </>
        )}
      </ReportStates>
      {first === undefined ? null : (
        <>
          <ReportActions
            reportKey="pharmacy-register"
            sheet={sheet}
            printBody={{
              period: first.period,
              facilityIds: [first.facility.facilityId],
              rowCount: rows.length,
            }}
            exportBody={shown}
            disabled={register.isFetching}
          />
          <PrintableReport
            sheet={sheet}
            title="Pharmacy register"
            facility={first.facility.name}
            period={periodWords(first.period)}
            dataAsAt={first.dataAsAt}
          >
            <PrintTable>
              <PharmacyHead />
              <tbody>
                {rows.map((r) => (
                  <PharmacyRow key={r.lineId} row={r} />
                ))}
              </tbody>
            </PrintTable>
          </PrintableReport>
        </>
      )}
    </RegisterShell>
  );
}

function PharmacyHead() {
  return (
    <thead>
      <tr>
        <th scope="col">Handed over</th>
        <th scope="col">Patient</th>
        <th scope="col">Item</th>
        <th scope="col" data-numeric>
          Quantity
        </th>
        <th scope="col">Batches</th>
        <th scope="col">Prescriber</th>
      </tr>
    </thead>
  );
}

function PharmacyRow({ row: r }: { row: PharmacyRegisterRow }) {
  return (
    <tr>
      <td>
        {formatInstant(r.at)}
        <span className={styles.secondLine}>
          {KIND_WORDS[r.kind]} <span className="wodi-id">{r.number}</span>
        </span>
        <span className={styles.secondLine}>{`By ${r.handedOverBy}`}</span>
      </td>
      <td>
        {r.patientName ?? 'Not identified'}
        {r.patientNumber === null ? null : (
          <span className={`${styles.secondLine} wodi-id`}>{r.patientNumber}</span>
        )}
      </td>
      <td>
        {r.itemName}
        <span className={`${styles.secondLine} wodi-id`}>{r.itemCode}</span>
        {r.controlled ? <span className={styles.secondLine}>Controlled</span> : null}
      </td>
      <td data-numeric>
        {`${String(r.quantity)} ${r.unit}`}
        {r.returnedQuantity > 0 ? (
          <span className={styles.secondLine}>{`${String(r.returnedQuantity)} returned`}</span>
        ) : null}
      </td>
      <td>
        <span className="wodi-id">{r.batchNumbers.join(', ')}</span>
      </td>
      <td>
        {r.prescriber ?? (r.kind === 'sale' ? 'Over the counter' : '')}
        {r.prescribedOutside ? (
          <span className={styles.secondLine}>Outside prescription</span>
        ) : null}
      </td>
    </tr>
  );
}
